"use client";

import { useState } from "react";
import Image, { type StaticImageData } from "next/image";
import { FiEdit3 } from "react-icons/fi";
import { IoArrowBack } from "react-icons/io5";
import { HiOutlineSquares2X2 } from "react-icons/hi2";
import { GamePreviewInline } from "../games/shared/GamePreviewViewport";
import {
    SmFindTheMatch,
    SmGameshowQuiz,
    SmFlipCards,
    SmAirPlanFlyingFruits,
    SmFlappyBird,
    SmWhackAMole,
    SmAnagram,
    SmCompleteTheSentence,
} from "../../assets";
import { useTranslations } from "next-intl";

const gameTypeSmallImageMap: Record<string, StaticImageData> = {
    find_the_match: SmFindTheMatch,
    game_show_quiz: SmGameshowQuiz,
    flip_cards: SmFlipCards,
    "air_plan/flying_fruits": SmAirPlanFlyingFruits,
    flappy_bird: SmFlappyBird,
    whack_a_mole: SmWhackAMole,
    anagram: SmAnagram,
    complete_the_sentence: SmCompleteTheSentence,
};

export type GameEditorWizardTarget = {
    uuid: string;
    gameType: string;
    source: string;
    gameId: number | null;
    title: string;
};

export type GameEditorWizardData = {
    target: GameEditorWizardTarget;
    previewUrl: string;
    thumbnailUrl: string;
    teacherName: string;
    gameTypeKey: string;
    gameTypeLabel: string;
    selectedGameTypeKey: string;
    hideGameTypePanel: boolean;
    swapGames: string[] | null;
};

type GameEditorWizardProps = {
    data: GameEditorWizardData;
    gameTypeOptions: Array<{ key: string; label: string }>;
    onSelectGameType: (gameTypeKey: string) => void;
    onClose: () => void;
    onConfirmEdit: () => void;
    onCreateNew: (gameTypeKey: string) => void | Promise<void>;
    onSwapAndEdit: (gameTypeKey: string) => Promise<void>;
};

export default function GameEditorWizard({
    data,
    gameTypeOptions,
    onSelectGameType,
    onClose,
    onConfirmEdit,
    onCreateNew,
    onSwapAndEdit,
}: GameEditorWizardProps) {
    const [expandedKey, setExpandedKey] = useState<string | null>(null);
    const [pendingSwapKey, setPendingSwapKey] = useState<string | null>(null);
    const [isSwapping, setIsSwapping] = useState(false);
    const [isCloning, setIsCloning] = useState(false);

    const tGEW = useTranslations("gameEditorWizard");

    const gameTypeQueryParam =
        data.selectedGameTypeKey === "air_plan/flying_fruits"
            ? "flying_fruits"
            : data.selectedGameTypeKey;
    const previewUrlWithGameType = gameTypeQueryParam
        ? `${data.previewUrl}${data.previewUrl.includes("?") ? "&" : "?"}${encodeURIComponent(gameTypeQueryParam)}`
        : data.previewUrl;

    const handleGameTypeClick = (key: string) => {
        onSelectGameType(key);
        setExpandedKey((prev) => (prev === key ? null : key));
    };

    const openConfirmPopup = (key: string) => {
        setPendingSwapKey(key);
    };

    const closeConfirmPopup = () => {
        if (isSwapping) return;
        setPendingSwapKey(null);
    };

    const handleCreateNew = async () => {
        if (!pendingSwapKey || isCloning) return;
        setIsCloning(true);
        try {
            await onCreateNew(pendingSwapKey);
        } finally {
            setIsCloning(false);
            setPendingSwapKey(null);
        }
    };

    const handleSwapAndEdit = async () => {
        if (!pendingSwapKey || isSwapping) return;
        setIsSwapping(true);
        try {
            await onSwapAndEdit(pendingSwapKey);
        } finally {
            setIsSwapping(false);
            setPendingSwapKey(null);
        }
    };

    const pendingLabel =
        gameTypeOptions.find((o) => o.key === pendingSwapKey)?.label ?? pendingSwapKey ?? "";

    return (
        <section className="flex h-full flex-col rounded-lg bg-white p-6 shadow-[inset_0_0_0_1px_rgba(0,0,0,0.04)]">
            <div className="flex flex-1 flex-col gap-4 lg:flex-row lg:items-start">
                <div className="flex flex-1 flex-col gap-3">
                    <GamePreviewInline
                        src={previewUrlWithGameType}
                        iframeTitle="Game Editor Preview"
                    />
                    <div className="flex items-center justify-between gap-3 px-4 py-3">
                        <div className="min-w-0">
                            <p className="truncate text-base font-semibold text-neutral-800">
                                {data.target.title || "-"}
                            </p>
                            {data.teacherName ? (
                                <p className="mt-0.5 text-sm text-neutral-500">{tGEW("by")} {data.teacherName}</p>
                            ) : null}
                            {data.gameTypeLabel ? (
                                <span className="mt-2 inline-block rounded bg-[#FDEF5C] px-2 py-0.5 text-[12px] leading-4 text-[#2b2b2b]">
                                    {data.gameTypeLabel}
                                </span>
                            ) : null}
                        </div>
                        <button
                            type="button"
                            onClick={onConfirmEdit}
                            className="flex cursor-pointer items-center gap-1.5 text-[#22A8DF] hover:text-[#1f97c8]"
                        >
                            <FiEdit3 className="text-base" />
                            <span className="text-sm font-semibold">{tGEW("editGame")}</span>
                        </button>
                    </div>
                </div>

                {!data.hideGameTypePanel ? (
                    <aside className="w-full shrink-0 rounded-2xl border border-neutral-200 bg-white p-4 shadow-xs lg:w-[320px]">
                        <h3 className="text-base font-semibold text-neutral-800">{tGEW("changeGameFormat")}</h3>
                        <p className="mt-1 text-md text-neutral-500">
                            {tGEW("whenChangeGameFormat1")}
                        </p>
                        <div className="mt-3 flex max-h-[68vh] flex-col gap-1 overflow-y-auto pr-1">
                            {gameTypeOptions.map(({ key, label }) => {
                                const isExpanded = expandedKey === key;
                                const image = gameTypeSmallImageMap[key];
                                return (
                                    <div key={key} className="flex flex-col">
                                        <button
                                            type="button"
                                            onClick={() => handleGameTypeClick(key)}
                                            className={`flex items-center gap-3 rounded-xl px-3 py-2 text-left transition-colors ${
                                                isExpanded
                                                    ? "bg-[#B7E6F8] text-neutral-800"
                                                    : "bg-white text-neutral-700 hover:bg-neutral-50"
                                            }`}
                                        >
                                            <span className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-[#1597CE] shadow-sm">
                                                {image ? (
                                                    <Image
                                                        src={image}
                                                        alt={label}
                                                        className="h-full w-full object-cover"
                                                    />
                                                ) : (
                                                    <HiOutlineSquares2X2 className="text-2xl text-white" />
                                                )}
                                            </span>
                                            <span className="text-sm font-medium">{label}</span>
                                        </button>

                                        {isExpanded ? (
                                            <div className="mx-3 mb-1 mt-1">
                                                <button
                                                    type="button"
                                                    onClick={() => openConfirmPopup(key)}
                                                    className="w-full cursor-pointer rounded-lg bg-[#22A8DF] px-3 py-1.5 text-sm font-semibold text-white hover:bg-[#1f97c8]"
                                                >
                                                    {tGEW("confirmCreateGame")}
                                                </button>
                                            </div>
                                        ) : null}
                                    </div>
                                );
                            })}
                        </div>
                    </aside>
                ) : null}
            </div>

            <div className="mt-4 flex items-center">
                <button
                    type="button"
                    onClick={onClose}
                    className="flex cursor-pointer items-center gap-2 rounded-lg px-3 py-1.5 text-sm font-semibold text-neutral-700 hover:bg-neutral-100"
                >
                    <IoArrowBack className="text-lg" />
                    {tGEW("back")}
                </button>
            </div>

            {pendingSwapKey ? (
                <div className="fixed inset-0 z-60 flex items-center justify-center bg-black/45 p-4">
                    <div className="w-full max-w-[460px] rounded-2xl bg-white px-8 pb-8 pt-7 shadow-[0_8px_32px_rgba(0,0,0,0.22)]">
                        <div className="mx-auto rounded-sm border border-[#FDEF5C] bg-[#FDEF5C] px-6 py-1.5 text-center text-lg font-semibold text-[#2B2B2B] shadow-[4px_4px_0_#5d6670]">
                            {tGEW("confirmChangeGameFormat")} "{pendingLabel}"
                        </div>
                        <p className="mt-6 text-center text-[15px] leading-7 text-[#4E4E4E]">
                            {tGEW("whenChangeGameFormat2")}<br />
                            {tGEW("systemCanCreateNewGame")}<br />
                            {tGEW("orChangeGameFormatDirectly")}
                        </p>
                        <div className="mt-7 flex justify-center gap-3">
                            <button
                                type="button"
                                onClick={() => void handleCreateNew()}
                                disabled={isSwapping || isCloning}
                                className="flex-1 cursor-pointer rounded-lg border-2 border-[#e8d200] bg-[#FDEF5C] py-2.5 text-[15px] font-semibold text-[#2B2B2B] shadow-[3px_3px_0_#a89c00] disabled:cursor-not-allowed disabled:opacity-70 hover:brightness-97 active:translate-y-px active:shadow-[1px_1px_0_#a89c00]"
                            >
                                {isCloning ? tGEW("creating") : tGEW("createNewGame")}
                            </button>
                            <button
                                type="button"
                                onClick={() => void handleSwapAndEdit()}
                                disabled={isSwapping}
                                className="flex-1 cursor-pointer rounded-lg bg-[#22A8DF] py-2.5 text-[15px] font-semibold text-white disabled:cursor-not-allowed disabled:opacity-70 hover:bg-[#1f97c8]"
                            >
                                {isSwapping ? tGEW("changing") : tGEW("changeFormat")}
                            </button>
                            <button
                                type="button"
                                onClick={closeConfirmPopup}
                                disabled={isSwapping || isCloning}
                                className="flex-1 cursor-pointer rounded-lg border border-[#c4c4c4] bg-white py-2.5 text-[15px] font-semibold text-[#4E4E4E] disabled:cursor-not-allowed disabled:opacity-70 hover:bg-neutral-50"
                            >
                                {tGEW("cancel")}
                            </button>
                        </div>
                    </div>
                </div>
            ) : null}
        </section>
    );
}
